import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { Button } from '~/ui/components/button';
import { Card, CardContent, CardHeader } from '~/ui/components/card';

export const Route = createFileRoute('/pricing')({
  head: () =>
    buildHead({
      title: 'Pricing - Free and Supporter',
      description:
        'Prod Battle is free to play - unlimited matches, uploads, and voting. The Supporter tier unlocks monthly playlists, early genre access, and community perks.',
      path: '/pricing',
    }),
  component: Pricing,
});

function Pricing() {
  const { data: session } = useSession();
  const isLoggedIn = session !== null && session !== undefined;
  const isSupporter = session?.plan === 'paid';
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const startCheckout = async () => {
    setCheckingOut(true);
    setCheckoutError(null);
    try {
      const { checkoutUrl } = await api<{ checkoutUrl: string }>('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ interval: 'monthly' }),
      });
      window.location.href = checkoutUrl;
    } catch (err) {
      setCheckingOut(false);
      const msg =
        err instanceof ApiError && err.status === 503
          ? 'Billing is not enabled yet. Check back soon.'
          : 'Could not start checkout. Try again in a moment.';
      setCheckoutError(msg);
    }
  };

  return (
    <section className="mx-auto max-w-4xl px-4 py-12 space-y-10">
      <header className="text-center space-y-3">
        <h1 className="text-3xl font-bold tracking-tight">Pricing</h1>
        <p className="text-muted-foreground">
          Free to play. Supporter unlocks monthly playlists and early access to new genres.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <PlanCard
          name="Guest"
          price="€0"
          tag="No account"
          features={[
            { ok: true, label: '3 matches per day' },
            { ok: true, label: 'Vote on live matches' },
            { ok: false, label: 'Upload sample packs' },
            { ok: false, label: 'Public profile' },
            { ok: false, label: 'Ranked leaderboard' },
          ]}
          cta={
            isLoggedIn ? (
              <Button asChild variant="outline" className="w-full" disabled>
                <span>You're signed in</span>
              </Button>
            ) : (
              <Button asChild variant="outline" className="w-full">
                <Link to="/play">Play as guest</Link>
              </Button>
            )
          }
        />
        <PlanCard
          name="Free"
          price="€0"
          tag={isLoggedIn ? 'Your plan' : 'Sign up free'}
          highlight={!isSupporter}
          features={[
            { ok: true, label: 'Unlimited matches' },
            { ok: true, label: 'Upload sample packs' },
            { ok: true, label: 'Public profile + tracks' },
            { ok: true, label: 'Ranked leaderboard per genre' },
            { ok: true, label: 'Vote and comment' },
          ]}
          cta={
            isLoggedIn ? (
              <Button asChild variant="outline" className="w-full">
                <Link to="/play">Go play</Link>
              </Button>
            ) : (
              <Button asChild className="w-full">
                <Link to="/auth/sign-up">Sign up free</Link>
              </Button>
            )
          }
        />
        <PlanCard
          name="Supporter"
          price="€2,95/mo"
          tag={isSupporter ? 'Your plan' : 'Help us grow'}
          highlight={isSupporter}
          features={[
            { ok: true, label: 'Everything in Free' },
            { ok: true, label: 'Monthly curated playlists' },
            { ok: true, label: 'Early access to new genres' },
            { ok: true, label: 'Supporter badge on profile' },
            { ok: true, label: 'Vote on roadmap items' },
          ]}
          cta={
            isSupporter ? (
              <Button asChild variant="outline" className="w-full">
                <Link to="/supporter/playlists">View playlists</Link>
              </Button>
            ) : isLoggedIn ? (
              <div className="space-y-2">
                <Button
                  className="w-full"
                  onClick={startCheckout}
                  disabled={checkingOut}
                  aria-label="Become a Supporter"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 className="size-4 animate-spin mr-1" />
                      Redirecting...
                    </>
                  ) : (
                    'Become a Supporter'
                  )}
                </Button>
                {checkoutError && (
                  <p className="text-xs text-destructive text-center">{checkoutError}</p>
                )}
              </div>
            ) : (
              <Button asChild variant="outline" className="w-full">
                <Link to="/auth/sign-up">Sign up first</Link>
              </Button>
            )
          }
        />
      </div>

      <section className="mx-auto max-w-2xl space-y-4 pt-8">
        <h2 className="text-xl font-semibold">Why this pricing?</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          We want every bedroom producer in the world to be able to compete, regardless of budget.
          The Free tier is fully featured forever. Supporter exists for people who want to chip in
          toward server costs and prize pools - it's a thank-you tier, not a paywall.
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed">
          We never gate match access, ranked play, or sample-pack uploads behind a subscription. If
          we ever do, we'll tell you in advance.
        </p>
      </section>
    </section>
  );
}

function PlanCard({
  name,
  price,
  tag,
  features,
  cta,
  highlight,
}: {
  name: string;
  price: string;
  tag: string;
  features: { ok: boolean; label: string }[];
  cta: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'border-primary' : ''}>
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {tag}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">{name}</span>
          <span className="text-muted-foreground text-sm">{price}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2 text-sm">
          {features.map((f) => (
            <li key={f.label} className="flex items-start gap-2">
              {f.ok ? (
                <Check className="size-4 text-primary mt-0.5 shrink-0" />
              ) : (
                <X className="size-4 text-muted-foreground/50 mt-0.5 shrink-0" />
              )}
              <span className={f.ok ? '' : 'text-muted-foreground/60'}>{f.label}</span>
            </li>
          ))}
        </ul>
        {cta}
      </CardContent>
    </Card>
  );
}
